import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, FolderInputIcon, InfoIcon, MinusIcon, PencilIcon, PlusIcon, StarIcon, Trash2Icon, XIcon } from 'lucide-react';
import { PreviewContent, pageCountFor } from './PreviewContent';
import { DetailsPanel } from './DetailsPanel';
import { FILE_TYPE_META } from '../../utils/fileTypes';
import { formatSize } from '../../utils/format';
import type { FsFile, FsNode } from '../../types/files';
interface PreviewOverlayProps {
  file: FsFile;
  nodes: FsNode[];
  position: {
    index: number;
    total: number;
  };
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onDownload: (node: FsNode) => void;
  onToggleStar: (node: FsNode) => void;
  onRename: (node: FsNode) => void;
  onMove: (node: FsNode) => void;
  onTrash: (node: FsNode) => void;
  onGoToFolder: (id: string | null) => void;
}
export function PreviewOverlay({
  file,
  nodes,
  position,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
  onDownload,
  onToggleStar,
  onRename,
  onMove,
  onTrash,
  onGoToFolder
}: PreviewOverlayProps) {
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [showDetails, setShowDetails] = useState(false);
  const pageCount = pageCountFor(file);
  const meta = FILE_TYPE_META[file.type];
  const Icon = meta.icon;
  useEffect(() => {
    setPage(1);
    setZoom(100);
  }, [file.id]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight' && hasNext) onNext();
      if (event.key === 'ArrowLeft' && hasPrev) onPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, onNext, onPrev, hasNext, hasPrev]);
  return <div role="dialog" aria-modal="true" aria-label={`Preview of ${file.name}`} className="fixed inset-0 z-40 flex flex-col bg-slate-900/95" data-id="element-272" data-mp="3ezkfo7rj7evi">
      <header className="flex items-center gap-3 border-b border-white/10 px-4 py-2.5" data-id="element-273" data-mp="1awhi5rv2fzk7">
        <Icon className="h-5 w-5 shrink-0 text-white/80" aria-hidden="true" data-id="element-274" data-mp="1auo8s4t2j18i" />
        <div className="min-w-0 flex-1" data-id="element-275" data-mp="1op74jxeib6hu">
          <h2 className="truncate text-sm font-semibold text-white" data-id="element-276" data-mp="1jxfle7xok31q">
            {file.name}.{file.extension}
          </h2>
          <p className="truncate text-xs text-white/60" data-id="element-277" data-mp="1yl0ow2ygw4a6">
            {meta.label} · {formatSize(file.sizeKb)} · v{file.version} ·{' '}
            {file.owner} · File {position.index} of {position.total}
          </p>
        </div>

        <div className="flex items-center gap-1" data-id="element-278" data-mp="27lysik4zu9s8">
          <OverlayButton label="Download" icon={DownloadIcon} onClick={() => onDownload(file)} primary data-id="element-279" data-mp="22n5col28i6ow" />
          <OverlayButton label={file.starred ? 'Remove star' : 'Add star'} icon={StarIcon} onClick={() => onToggleStar(file)} active={file.starred} data-id="element-280" data-mp="dak364fwacd0" />
          <OverlayButton label="Rename" icon={PencilIcon} onClick={() => onRename(file)} data-id="element-281" data-mp="2ow2r9qm1lo6y" />
          <OverlayButton label="Move to…" icon={FolderInputIcon} onClick={() => onMove(file)} data-id="element-282" data-mp="2b5h2xmdtkm1o" />
          <OverlayButton label="Move to Recycle Bin" icon={Trash2Icon} onClick={() => onTrash(file)} data-id="element-283" data-mp="2f46h3h6om7md" />
          <OverlayButton label={showDetails ? 'Hide details' : 'Show details'} icon={InfoIcon} onClick={() => setShowDetails(current => !current)} active={showDetails} data-id="element-284" data-mp="265usfkpdljwk" />
          <span className="mx-1 h-6 w-px bg-white/15" aria-hidden="true" data-id="element-285" data-mp="1w7x4yzfcbr50" />
          <OverlayButton label="Close preview" icon={XIcon} onClick={onClose} data-id="element-286" data-mp="3mjkvco4re24m" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1" data-id="element-287" data-mp="3qkejnim45iy1">
        <button type="button" onClick={onPrev} disabled={!hasPrev} aria-label="Previous file" className="hidden w-12 shrink-0 items-center justify-center text-white/70 transition hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:text-white/20 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 md:flex" data-id="element-288" data-mp="3qj3htoc8al9k">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-289" data-mp="2iwfwmyhvmz22" />
        </button>

        <div className="min-h-0 min-w-0 flex-1 overflow-auto" data-id="element-290" data-mp="2e5hctoxojiu2">
          <PreviewContent file={file} page={page} zoom={zoom} data-id="element-291" data-mp="2e3wedo88vfqx" />
        </div>

        <button type="button" onClick={onNext} disabled={!hasNext} aria-label="Next file" className="hidden w-12 shrink-0 items-center justify-center text-white/70 transition hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:text-white/20 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 md:flex" data-id="element-292" data-mp="296wc7oeimx53">
          <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-293" data-mp="aqdnizhx4xgm" />
        </button>

        {showDetails && <div className="hidden lg:block" data-id="element-294" data-mp="jozftmd6zqul">
            <DetailsPanel node={file} nodes={nodes} onClose={() => setShowDetails(false)} onDownload={onDownload} onToggleStar={onToggleStar} onRename={onRename} onMove={onMove} onTrash={onTrash} onGoToFolder={id => {
          onGoToFolder(id);
          onClose();
        }} data-id="element-295" data-mp="jq0sfv9uwlef" />
          </div>}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-4 py-2.5" data-id="element-296" data-mp="5pq00nsweyr5">
        <div className="flex items-center gap-1.5" data-id="element-297" data-mp="34fztdkshhu7j">
          <OverlayButton label="Previous page" icon={ChevronLeftIcon} onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page === 1} data-id="element-298" data-mp="34ddoxu0w3utj" />
          <span className="min-w-[7.5rem] text-center text-xs text-white/70" data-id="element-299" data-mp="2xc0660lxeyjo">
            {pageCount > 1 ? `Page ${page} of ${pageCount}` : 'Single page document'}
          </span>
          <OverlayButton label="Next page" icon={ChevronRightIcon} onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={page >= pageCount} data-id="element-300" data-mp="364lgqtfelyxf" />
        </div>

        <div className="flex items-center gap-1.5" data-id="element-301" data-mp="2eeqig79kiywm">
          <OverlayButton label="Zoom out" icon={MinusIcon} onClick={() => setZoom(current => Math.max(50, current - 25))} disabled={zoom <= 50} data-id="element-302" data-mp="2scnixer57pgk" />
          <span className="w-12 text-center text-xs tabular-nums text-white/70" data-id="element-303" data-mp="7m6j0kugxobu">
            {zoom}%
          </span>
          <OverlayButton label="Zoom in" icon={PlusIcon} onClick={() => setZoom(current => Math.min(200, current + 25))} disabled={zoom >= 200} data-id="element-304" data-mp="2mjtpd1d8hmo" />
        </div>

        <div className="flex items-center gap-2 md:hidden" data-id="element-305" data-mp="1plw39cxvsbh0">
          <button type="button" onClick={onPrev} disabled={!hasPrev} className="rounded border border-white/20 px-2.5 py-1 text-xs font-medium text-white/80 disabled:opacity-40" data-id="element-306" data-mp="1bl360kg732a7">
            Previous file
          </button>
          <button type="button" onClick={onNext} disabled={!hasNext} className="rounded border border-white/20 px-2.5 py-1 text-xs font-medium text-white/80 disabled:opacity-40" data-id="element-307" data-mp="28asvzcdmqvx4">
            Next file
          </button>
        </div>
      </footer>
    </div>;
}
interface OverlayButtonProps {
  label: string;
  icon: typeof DownloadIcon;
  onClick: () => void;
  primary?: boolean;
  active?: boolean;
  disabled?: boolean;
}
function OverlayButton({
  label,
  icon: Icon,
  onClick,
  primary,
  active,
  disabled
}: OverlayButtonProps) {
  return <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label} className={['inline-flex h-8 w-8 items-center justify-center rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70', primary ? 'bg-[#0176d3] text-white hover:bg-[#0b8ae8]' : active ? 'bg-white/20 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white', disabled ? 'cursor-not-allowed opacity-35 hover:bg-transparent' : ''].join(' ')} data-id="element-308" data-mp="n5bqb6f500n7">
      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-309" data-mp="wvwvxdt8a5fm" />
    </button>;
}